import { type IF2fTransactionItem } from "@gen/app-query";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";

import { CollapsibleSection } from "~/components/collapsible_section";
import { CopyButton } from "~/components/copy_button";
import { LedgerAmount } from "~/components/ledger_amount";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { ReadableDataGrid } from "~/components/readable_data_grid";
import { type Money, MoneyFactory } from "~/entities/money";

const THUMBNAIL_SIZE_PX = 44;

const ROW_HEIGHT_PX = 68;

const BILLING_LABELS_BY_RECURRING_PERIOD: Record<string, string> = {
  day: "Billed daily",
  week: "Billed weekly",
  month: "Billed monthly",
  year: "Billed yearly",
};

// `price` and `price_tax` are per unit, so the line total scales both by the
// quantity.
function getLineTotal(item: IF2fTransactionItem): Money {
  const quantity = item.quantity ?? 1;
  return new MoneyFactory(item.currency).create(
    (item.price + (item.price_tax ?? 0)) * quantity,
  );
}

function getBillingLabel(item: IF2fTransactionItem) {
  if (item.billing_type !== "recurring") {
    return "One-off";
  }
  return (
    BILLING_LABELS_BY_RECURRING_PERIOD[item.recurring_period ?? ""] ??
    "Recurring"
  );
}

// Every item repeats the same fields, so the item ID keeps each test ID unique
// on the page.
function itemTestId(item: IF2fTransactionItem, field: string, type: string) {
  return `items-section-${field}-${item.item_id}-${type}`;
}

interface ItemsSectionProps {
  items: IF2fTransactionItem[];
}

function ItemThumbnail({
  imageURL,
  alt,
  testId,
}: {
  imageURL: string;
  alt: string;
  testId: string;
}) {
  const [hasFailed, setHasFailed] = useState(false);

  return (
    <Box
      sx={{
        flex: `0 0 ${THUMBNAIL_SIZE_PX}px`,
        width: THUMBNAIL_SIZE_PX,
        height: THUMBNAIL_SIZE_PX,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.default",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {hasFailed ? (
        <PublicSvgIcon name="image_failed" />
      ) : (
        <Box
          component="img"
          src={imageURL}
          alt={alt}
          onError={() => setHasFailed(true)}
          data-testid={testId}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
    </Box>
  );
}

function AmountCell({
  item,
  field,
  amount,
}: {
  item: IF2fTransactionItem;
  field: string;
  amount?: number;
}) {
  return (
    <Typography variant="body2" data-testid={itemTestId(item, field, "text")}>
      {new MoneyFactory(item.currency).create(amount ?? 0).format()}
    </Typography>
  );
}

export function ItemsSection({ items }: ItemsSectionProps) {
  // Every item belongs to the same transaction, so they share its currency.
  const total = items
    .map(getLineTotal)
    .reduce((sum, lineTotal) => sum.add(lineTotal));

  const columns = useMemo<GridColDef<IF2fTransactionItem>[]>(
    () => [
      {
        field: "description",
        headerName: "Item",
        minWidth: 160,
        flex: 1.2,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ height: "100%", minWidth: 0 }}
            data-testid={`items-section-item-${row.item_id}`}
          >
            {row.image_url && (
              <ItemThumbnail
                imageURL={row.image_url}
                alt={row.description}
                testId={itemTestId(row, "image", "image")}
              />
            )}
            <Typography
              variant="body2"
              fontWeight="bold"
              noWrap
              data-testid={itemTestId(row, "description", "text")}
            >
              {row.description}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "billing_type",
        headerName: "Billing Period",
        minWidth: 140,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={getBillingLabel(row)}
            data-testid={itemTestId(row, "billing_type", "text")}
          />
        ),
      },
      {
        field: "ref_id",
        headerName: "Reference",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          row.ref_id ? (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{ height: "100%", minWidth: 0 }}
            >
              <Typography
                variant="body2"
                noWrap
                data-testid={itemTestId(row, "ref_id", "text")}
              >
                {row.ref_id}
              </Typography>
              <CopyButton
                value={row.ref_id}
                label="Copy reference"
                dataTestId={itemTestId(row, "ref_id", "copy-button")}
              />
            </Stack>
          ) : (
            "N/A"
          ),
      },
      {
        field: "tax_code",
        headerName: "Tax code",
        minWidth: 80,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            data-testid={itemTestId(row, "tax_code", "text")}
          >
            {row.tax_code || "N/A"}
          </Typography>
        ),
      },
      {
        field: "price",
        headerName: "Unit Price",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: ({ row }) => (
          <AmountCell item={row} field="price" amount={row.price} />
        ),
      },
      {
        field: "price_tax",
        headerName: "Tax",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: ({ row }) => (
          <AmountCell item={row} field="price_tax" amount={row.price_tax} />
        ),
      },
      {
        field: "price_extra",
        headerName: "Extra",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: ({ row }) => (
          <AmountCell item={row} field="price_extra" amount={row.price_extra} />
        ),
      },
      {
        field: "price_postage",
        headerName: "Postage",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: ({ row }) => (
          <AmountCell
            item={row}
            field="price_postage"
            amount={row.price_postage}
          />
        ),
      },
      {
        field: "total",
        headerName: "Total",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: ({ row }) => (
          <LedgerAmount
            amount={getLineTotal(row)}
            data-testid={itemTestId(row, "total", "text")}
          />
        ),
      },
    ],
    [],
  );

  return (
    <CollapsibleSection
      iconName="local_mall"
      title="Items"
      count={items.length}
      summary={
        <Stack alignItems="flex-end">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Items total
          </Typography>
          <LedgerAmount amount={total} variant="h5" component="span" />
        </Stack>
      }
      dataTestId="items-section"
    >
      <Box sx={{ backgroundColor: "common.white", borderRadius: 2 }}>
        <ReadableDataGrid<IF2fTransactionItem>
          dataTestId="items-section-grid"
          storageKey="ItemsSection"
          // The theme pads the footer for standalone grids, but here the section's
          // own bottom padding already provides the spacing.
          sx={{ "& .MuiDataGrid-footerContainer": { pb: 0 } }}
          columns={columns}
          rows={items}
          getRowId={(row) => row.item_id}
          rowHeight={ROW_HEIGHT_PX}
          // The total is what each row adds up to, so it stays in view while
          // the other columns scroll on narrow screens.
          pinnedColumns={{ right: ["total"] }}
          hideToolbar
          autoHeight
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          localeText={{
            noRowsLabel: "No items",
          }}
        />
      </Box>
    </CollapsibleSection>
  );
}
